import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Star } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'

import { Button } from '@turnifly/shared/ui'
import { triggerHapticImpact as hapticImpact, triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { useSubmitReview } from '@turnifly/shared/hooks'

import type { UserReview } from '@turnifly/shared/types'

// ── Types ────────────────────────────────────────────────────

interface ChallengeReviewSheetProps {
  isOpen: boolean
  onClose: () => void
  challengeId: string
  existingReview: UserReview | null
}

// ── Inner Form (remounts when sheet opens via key) ───────────

function ReviewForm({ onClose, challengeId, existingReview }: {
  onClose: () => void
  challengeId: string
  existingReview: UserReview | null
}): React.ReactElement {
  const { t } = useTranslation()
  const submitMutation = useSubmitReview()

  const [rating, setRating] = useState(existingReview?.rating ?? 0)
  const [text, setText] = useState(existingReview?.text ?? '')
  const [error, setError] = useState('')

  const handleStarClick = (star: number): void => {
    setRating(star)
    hapticImpact('light')
  }

  const handleSubmit = async (): Promise<void> => {
    if (rating < 1) {
      setError(t('challenges.profile.starRating'))
      return
    }
    try {
      await submitMutation.mutateAsync({
        challengeId,
        rating,
        text: text.trim() || null,
      })
      hapticNotification('success')
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('common.error')
      setError(msg)
    }
  }

  return (
    <div className="px-5 pt-4 pb-6 space-y-5">
      <h3 className="text-[16px] font-display font-bold text-text text-center">
        {existingReview ? t('challenges.profile.editReview') : t('challenges.profile.writeReview')}
      </h3>

      {/* Stars */}
      <div className="flex justify-center gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => handleStarClick(star)}
            className="tap-highlight-transparent p-1"
          >
            <Star
              size={32}
              className={star <= rating ? 'text-[#FFD700] fill-[#FFD700]' : 'text-text-hint/30'}
            />
          </button>
        ))}
      </div>

      {/* Text */}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 500))}
        placeholder={t('challenges.profile.reviewPlaceholder')}
        rows={3}
        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-3.5 py-2.5 text-[13px] text-text placeholder:text-text-hint/40 resize-none focus:outline-none focus:border-primary/30"
      />
      <div className="text-right text-[10px] text-text-hint/40 -mt-3">{text.length}/500</div>

      {error && <p className="text-[12px] text-danger text-center">{error}</p>}

      <Button
        variant="primary"
        fullWidth
        isLoading={submitMutation.isPending}
        onClick={handleSubmit}
        disabled={rating < 1}
      >
        {existingReview ? t('common.save') : t('challenges.profile.writeReview')}
      </Button>
    </div>
  )
}

// ── Wrapper (AnimatePresence + backdrop) ─────────────────────

export const ChallengeReviewSheet: React.FC<ChallengeReviewSheetProps> = ({
  isOpen,
  onClose,
  challengeId,
  existingReview,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-bg rounded-t-2xl border-t border-white/[0.06] pb-safe"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            <div className="w-10 h-1 bg-white/10 rounded-full mx-auto mt-3" />
            <ReviewForm
              key={String(isOpen)}
              onClose={onClose}
              challengeId={challengeId}
              existingReview={existingReview}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
